import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { MapPin, Sun, Shield, Bed, Bath, Square, ShoppingBag, Mountain, GraduationCap, TreePine, TrendingUp, Home, Heart, Car, Plane, Utensils, Crown, Eye, Trophy } from 'lucide-react';
import { ContactCTA } from '../components/ContactCTA';
import { ListingsComingSoon } from '../components/ListingsComingSoon';
import { SEOHead } from '../components/SEOHead';
import { RelatedCommunities } from '../components/RelatedCommunities';
const placeholderListings = [{
  id: 1,
  price: '$3,250,000',
  address: '552 Dragon Peak Dr',
  area: 'MacDonald Highlands',
  beds: 5,
  baths: 6,
  sqft: '5,800',
  image: 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?q=80&w=2940&auto=format&fit=crop'
}, {
  id: 2,
  price: '$1,850,000',
  address: '1489 Foothills Village Dr',
  area: 'MacDonald Highlands',
  beds: 4,
  baths: 4.5,
  sqft: '4,200',
  image: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?q=80&w=2950&auto=format&fit=crop'
}, {
  id: 3,
  price: '$4,250,000',
  address: '789 Dragon Peak Dr',
  area: 'MacDonald Highlands',
  beds: 6,
  baths: 8,
  sqft: '8,500',
  image: 'https://images.unsplash.com/photo-1613490908571-9ce224083933?q=80&w=2838&auto=format&fit=crop'
}];
export function MacDonaldHighlandsPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <main className="bg-white" data-id="element-982">
      <SEOHead title="MacDonald Highlands Real Estate & Luxury Estates | Jenny Gong" description="Explore ultra-luxury real estate in MacDonald Highlands. An exclusive gated enclave in Henderson offering panoramic city views and the DragonRidge Country Club." keywords="MacDonald Highlands real estate, MacDonald Highlands homes for sale, DragonRidge Country Club real estate, Henderson luxury estates, Las Vegas luxury homes" canonicalUrl="https://jennygong.com/macdonald-highlands" ogImage="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=80&w=2940&auto=format&fit=crop" data-id="element-983" />
      <section className="relative h-[70vh] min-h-[600px] w-full flex items-center justify-center overflow-hidden" data-id="element-984">
        <div className="absolute inset-0 w-full h-full bg-cover bg-center bg-no-repeat" style={{
        backgroundImage: 'url("https://images.unsplash.com/photo-1613490908571-9ce224083933?q=80&w=2838&auto=format&fit=crop")'
      }} data-id="element-985">
          <div className="absolute inset-0 bg-charcoal/40 mix-blend-multiply" data-id="element-986"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-charcoal/60 via-transparent to-charcoal/80" data-id="element-987"></div>
        </div>
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto mt-20" data-id="element-988">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1
        }} data-id="element-989">
            <span className="text-gold uppercase tracking-[0.3em] text-sm font-medium mb-6 block" data-id="element-990">
              Community Spotlight
            </span>
            <h1 className="text-5xl md:text-7xl text-white font-serif mb-6 leading-tight" data-id="element-991">
              MacDonald Highlands Homes for Sale
            </h1>
            <p className="text-lg md:text-xl text-white/90 font-light max-w-2xl mx-auto" data-id="element-992">
              Ultra-luxury gated enclave in Henderson with the most breathtaking
              panoramic views in all of Las Vegas.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-24 px-6 bg-white" data-id="element-993">
        <div className="max-w-7xl mx-auto" data-id="element-994">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-995">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} data-id="element-996">
              <h2 className="text-3xl md:text-4xl font-serif text-charcoal mb-6" data-id="element-997">
                The Crown Jewel of{' '}
                <span className="italic text-gold" data-id="element-998">Henderson</span>
              </h2>
              <div className="space-y-4 text-charcoal-light font-light leading-relaxed text-lg" data-id="element-999">
                <p data-id="element-1000">
                  MacDonald Highlands is the most exclusive residential
                  community in the Las Vegas Valley. Perched high in the
                  McCullough Range above{' '}
                  <Link to="/henderson" className="text-gold hover:underline" data-id="element-1001">
                    Henderson
                  </Link>
                  , this guard-gated enclave offers jaw-dropping 360-degree
                  panoramic views of the entire Las Vegas Strip, the surrounding
                  mountains, and Lake Mead in the distance.
                </p>
                <p data-id="element-1002">
                  Developed by MacDonald Properties, the community features
                  custom homesites ranging from half an acre to over two acres,
                  with homes designed by the region's most acclaimed architects.
                  Prices range from $1.5M to over $15M for the most spectacular
                  estates. For additional luxury options in the area, explore
                  nearby{' '}
                  <Link to="/green-valley" className="text-gold hover:underline" data-id="element-1003">
                    Green Valley
                  </Link>{' '}
                  and{' '}
                  <Link to="/southern-highlands" className="text-gold hover:underline" data-id="element-1004">
                    Southern Highlands
                  </Link>
                  .
                </p>
                <p data-id="element-1005">
                  At the heart of the community is DragonRidge Country Club, one
                  of the most prestigious private clubs in Nevada, offering
                  world-class golf, dining, fitness, and social events in an
                  unmatched setting.
                </p>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="grid grid-cols-2 gap-6" data-id="element-1006">
              <div className="bg-warmGray p-8 text-center border border-warmGray-dark" data-id="element-1007">
                <Crown className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-1008" />
                <h4 className="text-3xl font-serif text-charcoal mb-2" data-id="element-1009">
                  Ultra
                </h4>
                <p className="text-sm text-charcoal-light uppercase tracking-wider" data-id="element-1010">
                  Luxury
                </p>
              </div>
              <div className="bg-warmGray p-8 text-center border border-warmGray-dark" data-id="element-1011">
                <Eye className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-1012" />
                <h4 className="text-3xl font-serif text-charcoal mb-2" data-id="element-1013">360°</h4>
                <p className="text-sm text-charcoal-light uppercase tracking-wider" data-id="element-1014">
                  Strip Views
                </p>
              </div>
              <div className="bg-warmGray p-8 text-center border border-warmGray-dark" data-id="element-1015">
                <Shield className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-1016" />
                <h4 className="text-3xl font-serif text-charcoal mb-2" data-id="element-1017">24/7</h4>
                <p className="text-sm text-charcoal-light uppercase tracking-wider" data-id="element-1018">
                  Guard Gated
                </p>
              </div>
              <div className="bg-warmGray p-8 text-center border border-warmGray-dark" data-id="element-1019">
                <Trophy className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-1020" />
                <h4 className="text-3xl font-serif text-charcoal mb-2" data-id="element-1021">18</h4>
                <p className="text-sm text-charcoal-light uppercase tracking-wider" data-id="element-1022">
                  Hole Golf
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* DragonRidge */}
      <section className="py-24 bg-warmGray px-6 border-y border-warmGray-dark" data-id="element-1023">
        <div className="max-w-7xl mx-auto" data-id="element-1024">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-1025">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="order-2 lg:order-1" data-id="element-1026">
              <div className="relative h-[500px]" data-id="element-1027">
                <img src="https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa?q=80&w=2940&auto=format&fit=crop" alt="DragonRidge Golf" className="w-full h-full object-cover" data-id="element-1028" />
                <div className="absolute inset-0 border border-gold/30 m-4 pointer-events-none" data-id="element-1029"></div>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="order-1 lg:order-2" data-id="element-1030">
              <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-1031">
                Private Club
              </span>
              <h2 className="text-3xl md:text-4xl font-serif text-charcoal mb-6" data-id="element-1032">
                DragonRidge Country Club
              </h2>
              <div className="space-y-4 text-charcoal-light font-light leading-relaxed text-lg" data-id="element-1033">
                <p data-id="element-1034">
                  DragonRidge Country Club is the crown jewel of MacDonald
                  Highlands. The 18-hole championship course, designed by Jay
                  Morrish and David Druzisky, winds dramatically through the
                  mountains with elevation changes of over 300 feet — creating
                  one of the most visually stunning golf experiences in the
                  world.
                </p>
                <p data-id="element-1035">
                  The 47,000 square foot clubhouse features fine dining, a
                  full-service spa, resort-style pool, tennis courts, and a
                  state-of-the-art fitness center. Members enjoy an intimate,
                  exclusive atmosphere with only 400 memberships available.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* The Views */}
      <section className="py-24 px-6 bg-white" data-id="element-1036">
        <div className="max-w-7xl mx-auto" data-id="element-1037">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-1038">
            <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-1039">
              Unmatched Vistas
            </span>
            <h2 className="text-4xl font-serif text-charcoal" data-id="element-1040">
              The Views Define the Lifestyle
            </h2>
            <p className="text-charcoal-light font-light max-w-3xl mx-auto mt-4 text-lg" data-id="element-1041">
              What truly sets MacDonald Highlands apart is the view. From nearly
              every home, you can see the glittering Las Vegas Strip stretching
              across the valley floor, framed by the Spring Mountains to the
              west and Lake Mead to the east. At night, the city transforms into
              a sea of lights that rivals any view in the world.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1042">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="group" data-id="element-1043">
              <div className="relative h-64 mb-6 overflow-hidden" data-id="element-1044">
                <img src="https://images.unsplash.com/photo-1512917774080-9991f1c4c750?q=80&w=2940&auto=format&fit=crop" alt="Luxury Estate" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-1045" />
              </div>
              <h3 className="text-2xl font-serif text-charcoal mb-3" data-id="element-1046">
                Custom Estates
              </h3>
              <p className="text-charcoal-light font-light text-sm" data-id="element-1047">
                Architecturally stunning homes designed by the valley's top
                architects, featuring infinity pools, home theaters, and wine
                cellars.
              </p>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="group" data-id="element-1048">
              <div className="relative h-64 mb-6 overflow-hidden" data-id="element-1049">
                <img src="https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?q=80&w=2874&auto=format&fit=crop" alt="Interior" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-1050" />
              </div>
              <h3 className="text-2xl font-serif text-charcoal mb-3" data-id="element-1051">
                Designer Interiors
              </h3>
              <p className="text-charcoal-light font-light text-sm" data-id="element-1052">
                Homes feature the finest finishes — imported stone, custom
                millwork, smart home technology, and floor-to-ceiling glass to
                capture the views.
              </p>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3
          }} className="group" data-id="element-1053">
              <div className="relative h-64 mb-6 overflow-hidden" data-id="element-1054">
                <img src="https://images.unsplash.com/photo-1540539234-c14a20fb7c7b?q=80&w=2940&auto=format&fit=crop" alt="Spa" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-1055" />
              </div>
              <h3 className="text-2xl font-serif text-charcoal mb-3" data-id="element-1056">
                Private Amenities
              </h3>
              <p className="text-charcoal-light font-light text-sm" data-id="element-1057">
                Many estates include private spas, putting greens, outdoor
                kitchens, and resort-style backyards with Strip views.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Location & Nearby */}
      <section className="py-24 bg-warmGray px-6 border-y border-warmGray-dark" data-id="element-1058">
        <div className="max-w-7xl mx-auto" data-id="element-1059">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-1060">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="space-y-8" data-id="element-1061">
              <div data-id="element-1062">
                <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-1063">
                  Prime Location
                </span>
                <h2 className="text-3xl md:text-4xl font-serif text-charcoal mb-6" data-id="element-1064">
                  Minutes From Everything
                </h2>
                <p className="text-charcoal-light font-light leading-relaxed text-lg" data-id="element-1065">
                  Despite its secluded, mountaintop feel, MacDonald Highlands is
                  just minutes from Henderson's best amenities and a short drive
                  to the Las Vegas Strip.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-1066">
                <div className="bg-white p-6 border border-warmGray-dark" data-id="element-1067">
                  <Utensils className="w-6 h-6 text-gold mb-3" data-id="element-1068" />
                  <h4 className="font-serif text-lg text-charcoal mb-2" data-id="element-1069">
                    Fine Dining
                  </h4>
                  <p className="text-charcoal-light font-light text-sm" data-id="element-1070">
                    Minutes from Henderson's top restaurants including Todd's
                    Unique Dining, Bottiglia, and the M Resort dining
                    collection.
                  </p>
                </div>
                <div className="bg-white p-6 border border-warmGray-dark" data-id="element-1071">
                  <ShoppingBag className="w-6 h-6 text-gold mb-3" data-id="element-1072" />
                  <h4 className="font-serif text-lg text-charcoal mb-2" data-id="element-1073">
                    The District
                  </h4>
                  <p className="text-charcoal-light font-light text-sm" data-id="element-1074">
                    Green Valley's premier open-air shopping and dining
                    destination is just a 10-minute drive down the hill.
                  </p>
                </div>
                <div className="bg-white p-6 border border-warmGray-dark" data-id="element-1075">
                  <Waves className="w-6 h-6 text-gold mb-3" data-id="element-1076" />
                  <h4 className="font-serif text-lg text-charcoal mb-2" data-id="element-1077">
                    Lake Mead
                  </h4>
                  <p className="text-charcoal-light font-light text-sm" data-id="element-1078">
                    Just 20 minutes to Lake Mead National Recreation Area for
                    boating, fishing, and stunning desert waterscapes.
                  </p>
                </div>
                <div className="bg-white p-6 border border-warmGray-dark" data-id="element-1079">
                  <Mountain className="w-6 h-6 text-gold mb-3" data-id="element-1080" />
                  <h4 className="font-serif text-lg text-charcoal mb-2" data-id="element-1081">
                    Sloan Canyon
                  </h4>
                  <p className="text-charcoal-light font-light text-sm" data-id="element-1082">
                    Nearby national conservation area with ancient petroglyphs
                    and world-class desert hiking trails.
                  </p>
                </div>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} data-id="element-1083">
              <div className="relative h-[600px]" data-id="element-1084">
                <img src="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?q=80&w=2940&auto=format&fit=crop" alt="Mountain Views Henderson" className="w-full h-full object-cover" data-id="element-1085" />
                <div className="absolute inset-0 border border-gold/30 m-4 pointer-events-none" data-id="element-1086"></div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Exclusive Living */}
      <section className="py-24 px-6 bg-white" data-id="element-1087">
        <div className="max-w-4xl mx-auto text-center" data-id="element-1088">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-1089">
            <Users className="w-12 h-12 text-gold mx-auto mb-6" data-id="element-1090" />
            <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-1091">
              An Elite Address
            </span>
            <h2 className="text-4xl font-serif text-charcoal mb-6" data-id="element-1092">
              Who Lives in MacDonald Highlands
            </h2>
            <p className="text-charcoal-light font-light leading-relaxed text-lg mb-8" data-id="element-1093">
              MacDonald Highlands attracts a discerning clientele of successful
              entrepreneurs, executives, professional athletes, and entertainers
              who value privacy, exclusivity, and the finest things in life. The
              community's limited number of homesites ensures an intimate
              atmosphere where neighbors become friends.
            </p>
            <p className="text-charcoal-light font-light leading-relaxed text-lg mb-12" data-id="element-1094">
              With 24/7 guard-gated security, private roads, and a community
              designed for those who have achieved the highest levels of
              success, MacDonald Highlands isn't just a place to live — it's a
              statement. The community regularly hosts exclusive social events
              at DragonRidge, fostering a tight-knit network of like-minded
              residents.
            </p>
            <div className="flex flex-wrap justify-center gap-8" data-id="element-1095">
              <div className="flex items-center gap-2 text-charcoal" data-id="element-1096">
                <Star className="w-5 h-5 text-gold" data-id="element-1097" />
                <span className="font-serif" data-id="element-1098">Entrepreneurs & Executives</span>
              </div>
              <div className="flex items-center gap-2 text-charcoal" data-id="element-1099">
                <Star className="w-5 h-5 text-gold" data-id="element-1100" />
                <span className="font-serif" data-id="element-1101">Professional Athletes</span>
              </div>
              <div className="flex items-center gap-2 text-charcoal" data-id="element-1102">
                <Star className="w-5 h-5 text-gold" data-id="element-1103" />
                <span className="font-serif" data-id="element-1104">Entertainment Industry</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Education */}
      <section className="py-24 bg-warmGray px-6 border-t border-warmGray-dark" data-id="element-1105">
        <div className="max-w-7xl mx-auto" data-id="element-1106">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-1107">
            <GraduationCap className="w-12 h-12 text-gold mx-auto mb-6" data-id="element-1108" />
            <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-1109">
              Education
            </span>
            <h2 className="text-4xl font-serif text-charcoal mb-6" data-id="element-1110">
              Elite Educational Options
            </h2>
            <p className="text-charcoal-light font-light max-w-3xl mx-auto text-lg leading-relaxed" data-id="element-1111">
              MacDonald Highlands residents have access to some of the most
              prestigious private academies and top-rated public schools in
              Southern Nevada.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1112">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="bg-white p-8 border border-warmGray-dark" data-id="element-1113">
              <h3 className="text-xl font-serif text-charcoal mb-4 border-b border-gold/20 pb-4" data-id="element-1114">
                Private Academies
              </h3>
              <ul className="space-y-3 text-charcoal-light font-light text-sm" data-id="element-1115">
                <li data-id="element-1116">• Henderson International School</li>
                <li data-id="element-1117">• Bishop Gorman High School (Nearby)</li>
                <li data-id="element-1118">• The Meadows School (Via 215)</li>
                <li data-id="element-1119">• Coral Academy of Science</li>
                <li className="pt-4 italic text-xs text-gold" data-id="element-1120">
                  Many residents utilize private transportation services to
                  elite prep schools across the valley.
                </li>
              </ul>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="bg-white p-8 border border-warmGray-dark" data-id="element-1121">
              <h3 className="text-xl font-serif text-charcoal mb-4 border-b border-gold/20 pb-4" data-id="element-1122">
                Top Public Schools
              </h3>
              <ul className="space-y-3 text-charcoal-light font-light text-sm" data-id="element-1123">
                <li data-id="element-1124">• John C. Vanderburg Elementary (Blue Ribbon)</li>
                <li data-id="element-1125">• Neil C. Twitchell Elementary</li>
                <li data-id="element-1126">• Bob Miller Middle School</li>
                <li data-id="element-1127">• Foothill High School</li>
                <li data-id="element-1128">• Coronado High School (Nearby)</li>
              </ul>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3
          }} className="bg-white p-8 border border-warmGray-dark" data-id="element-1129">
              <h3 className="text-xl font-serif text-charcoal mb-4 border-b border-gold/20 pb-4" data-id="element-1130">
                Higher Education
              </h3>
              <ul className="space-y-3 text-charcoal-light font-light text-sm" data-id="element-1131">
                <li data-id="element-1132">• UNLV (University of Nevada, Las Vegas)</li>
                <li data-id="element-1133">• Nevada State University</li>
                <li data-id="element-1134">• Roseman University of Health Sciences</li>
                <li data-id="element-1135">• Touro University Nevada</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Parks & Recreation */}
      <section className="py-24 px-6 bg-white" data-id="element-1136">
        <div className="max-w-7xl mx-auto" data-id="element-1137">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-1138">
            <TreePine className="w-12 h-12 text-gold mx-auto mb-6" data-id="element-1139" />
            <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-1140">
              Outdoor Living
            </span>
            <h2 className="text-4xl font-serif text-charcoal" data-id="element-1141">
              Parks & Recreation
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1142">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="group" data-id="element-1143">
              <div className="relative h-64 mb-6 overflow-hidden" data-id="element-1144">
                <img src="https://images.unsplash.com/photo-1519331379826-f10be5486c6f?q=80&w=2940&auto=format&fit=crop" alt="Private Community Parks" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-1145" />
              </div>
              <h3 className="text-2xl font-serif text-charcoal mb-3" data-id="element-1146">
                Private Community Parks
              </h3>
              <p className="text-charcoal-light font-light text-sm leading-relaxed" data-id="element-1147">
                MacDonald Highlands features several private, meticulously
                landscaped parks exclusively for residents, offering serene
                walking paths, water features, and shaded seating areas.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="group" data-id="element-1148">
              <div className="relative h-64 mb-6 overflow-hidden" data-id="element-1149">
                <img src="https://images.unsplash.com/photo-1509316785289-025f5b846b35?q=80&w=2940&auto=format&fit=crop" alt="Sloan Canyon National Conservation Area desert hiking trails near MacDonald Highlands Henderson" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-1150" />
              </div>
              <h3 className="text-2xl font-serif text-charcoal mb-3" data-id="element-1151">
                Sloan Canyon
              </h3>
              <p className="text-charcoal-light font-light text-sm leading-relaxed" data-id="element-1152">
                Bordering the community to the south, this 48,000-acre
                conservation area offers miles of pristine hiking trails and
                ancient petroglyphs right in your backyard.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3
          }} className="group" data-id="element-1153">
              <div className="relative h-64 mb-6 overflow-hidden" data-id="element-1154">
                <img src="https://images.unsplash.com/photo-1542273917363-3b1817f69a2d?q=80&w=2948&auto=format&fit=crop" alt="DragonRidge Tennis & Athletic Center" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-1155" />
              </div>
              <h3 className="text-2xl font-serif text-charcoal mb-3" data-id="element-1156">
                DragonRidge Athletic Center
              </h3>
              <p className="text-charcoal-light font-light text-sm leading-relaxed" data-id="element-1157">
                A world-class facility featuring lighted tennis courts, a
                resort-style swimming pool, state-of-the-art fitness equipment,
                and personal training services.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.4
          }} className="group" data-id="element-1158">
              <div className="relative h-64 mb-6 overflow-hidden" data-id="element-1159">
                <img src="https://images.unsplash.com/photo-1472214103451-9374bd1c798e?q=80&w=2940&auto=format&fit=crop" alt="McCullough Mountains Trail System" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-1160" />
              </div>
              <h3 className="text-2xl font-serif text-charcoal mb-3" data-id="element-1161">
                McCullough Mountains Trails
              </h3>
              <p className="text-charcoal-light font-light text-sm leading-relaxed" data-id="element-1162">
                Exclusive access to rugged mountain trails offering challenging
                hikes and unparalleled vantage points overlooking the entire Las
                Vegas Valley.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Shopping & Dining */}
      <section className="py-24 bg-warmGray px-6 border-y border-warmGray-dark" data-id="element-1163">
        <div className="max-w-7xl mx-auto" data-id="element-1164">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-1165">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="order-2 lg:order-1" data-id="element-1166">
              <div className="relative h-[600px]" data-id="element-1167">
                <img src="https://images.unsplash.com/photo-1555396273-367ea4eb4db5?q=80&w=2874&auto=format&fit=crop" alt="Fine Dining" className="w-full h-full object-cover" data-id="element-1168" />
                <div className="absolute inset-0 border border-gold/30 m-4 pointer-events-none" data-id="element-1169"></div>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="order-1 lg:order-2" data-id="element-1170">
              <ShoppingBag className="w-10 h-10 text-gold mb-6" data-id="element-1171" />
              <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-1172">
                Lifestyle & Culinary
              </span>
              <h2 className="text-3xl md:text-4xl font-serif text-charcoal mb-8" data-id="element-1173">
                Shopping & Dining
              </h2>

              <div className="space-y-8" data-id="element-1174">
                <div data-id="element-1175">
                  <h4 className="font-serif text-xl text-charcoal mb-2 flex items-center gap-2" data-id="element-1176">
                    <Utensils className="w-5 h-5 text-gold" data-id="element-1177" />
                    DragonRidge Clubhouse Dining
                  </h4>
                  <p className="text-charcoal-light font-light text-sm leading-relaxed" data-id="element-1178">
                    Exclusive fine dining right within the community, featuring
                    seasonal menus, a sophisticated lounge, and breathtaking
                    views of the Strip from the terrace.
                  </p>
                </div>

                <div data-id="element-1179">
                  <h4 className="font-serif text-xl text-charcoal mb-2 flex items-center gap-2" data-id="element-1180">
                    <ShoppingBag className="w-5 h-5 text-gold" data-id="element-1181" />
                    The District at Green Valley Ranch
                  </h4>
                  <p className="text-charcoal-light font-light text-sm leading-relaxed" data-id="element-1182">
                    Just minutes down the hill, offering upscale boutique
                    shopping, Whole Foods Market, and a variety of excellent
                    restaurants in a pedestrian-friendly setting.
                  </p>
                </div>

                <div data-id="element-1183">
                  <h4 className="font-serif text-xl text-charcoal mb-2 flex items-center gap-2" data-id="element-1184">
                    <Utensils className="w-5 h-5 text-gold" data-id="element-1185" />
                    Green Valley Ranch Resort
                  </h4>
                  <p className="text-charcoal-light font-light text-sm leading-relaxed" data-id="element-1186">
                    A luxury resort offering world-class steakhouses, seafood,
                    and Italian dining, plus a luxurious spa and entertainment
                    options.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Healthcare */}
      <section className="py-24 px-6 bg-white" data-id="element-1187">
        <div className="max-w-4xl mx-auto text-center" data-id="element-1188">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-1189">
            <Heart className="w-12 h-12 text-gold mx-auto mb-6" data-id="element-1190" />
            <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-1191">
              Wellness
            </span>
            <h2 className="text-4xl font-serif text-charcoal mb-6" data-id="element-1192">
              World-Class Healthcare
            </h2>
            <p className="text-charcoal-light font-light leading-relaxed text-lg mb-8" data-id="element-1193">
              MacDonald Highlands residents demand the best, and the surrounding
              medical infrastructure delivers. The community is just minutes
              from the prestigious{' '}
              <strong data-id="element-1194">St. Rose Dominican Hospital - Siena Campus</strong>, known
              for its advanced cardiac and neurological care.
            </p>
            <p className="text-charcoal-light font-light leading-relaxed text-lg" data-id="element-1195">
              Additionally, the nearby <strong data-id="element-1196">Henderson Hospital</strong> and
              numerous private concierge medical practices, specialized clinics,
              and luxury wellness spas ensure residents receive unparalleled
              care and privacy.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Transportation */}
      <section className="py-24 bg-charcoal px-6 text-white" data-id="element-1197">
        <div className="max-w-7xl mx-auto" data-id="element-1198">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-1199">
            <MapPin className="w-12 h-12 text-gold mx-auto mb-6" data-id="element-1200" />
            <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-1201">
              Connectivity
            </span>
            <h2 className="text-4xl font-serif mb-6" data-id="element-1202">
              Transportation & Access
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-1203">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="bg-white/5 p-8 border border-white/10 text-center" data-id="element-1204">
              <Plane className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-1205" />
              <h4 className="text-xl font-serif mb-2" data-id="element-1206">
                Henderson Exec Airport
              </h4>
              <p className="text-white/60 font-light text-sm" data-id="element-1207">
                A premier private aviation facility just 15 minutes away,
                offering discreet and luxurious travel for private jet owners.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="bg-white/5 p-8 border border-white/10 text-center" data-id="element-1208">
              <Plane className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-1209" />
              <h4 className="text-xl font-serif mb-2" data-id="element-1210">Harry Reid Airport</h4>
              <p className="text-white/60 font-light text-sm" data-id="element-1211">
                The main international hub is an easy 15-20 minute drive via the
                215 Beltway.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3
          }} className="bg-white/5 p-8 border border-white/10 text-center" data-id="element-1212">
              <Car className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-1213" />
              <h4 className="text-xl font-serif mb-2" data-id="element-1214">215 Beltway</h4>
              <p className="text-white/60 font-light text-sm" data-id="element-1215">
                Immediate access at Valle Verde or Stephanie provides rapid
                transit across the entire Las Vegas Valley.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.4
          }} className="bg-white/5 p-8 border border-white/10 text-center" data-id="element-1216">
              <MapPin className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-1217" />
              <h4 className="text-xl font-serif mb-2" data-id="element-1218">The Strip</h4>
              <p className="text-white/60 font-light text-sm" data-id="element-1219">
                Just 20 minutes away, allowing residents to enjoy world-class
                entertainment and retreat to their mountain sanctuary.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      <RelatedCommunities currentCommunity="MacDonald Highlands" data-id="element-1220" />

      {/* Listings */}
      <ListingsComingSoon area="MacDonald Highlands" data-id="element-1221" />

      <ContactCTA data-id="element-1222" />
    </main>;
}